import Cookies from "js-cookie";

import {
  extractInviteCodeFromDeepLink,
  extractInviteCodeFromPathname,
  normalizeInviteCode,
  INVITE_CODE_COOKIE_KEY,
  INVITE_CODE_QUERY_KEY,
} from "./inviteCode";
import {
  clearInviteCodeInStore,
  setInviteCodeInStore,
} from "./inviteCodeStore";

/**
 * 邀请码 cookie 读写 + 捕获（客户端专用）。
 *
 * 对齐 `utils/onboarding/adSourceCookie.client.ts` 的形态，但有一处**关键差异**：
 * 邀请码大小写敏感，不做 `toUpperCase()`。
 */

const COOKIE_OPTIONS: Cookies.CookieAttributes = {
  expires: 365,
  path: "/",
  sameSite: "Lax",
};

function withSecureFlag(): Cookies.CookieAttributes {
  if (typeof window === "undefined") {
    return COOKIE_OPTIONS;
  }
  return {
    ...COOKIE_OPTIONS,
    secure: window.location.protocol === "https:",
  };
}

/** 读取本地邀请码 cookie；无则 null。 */
export function getInviteCodeCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  return normalizeInviteCode(Cookies.get(INVITE_CODE_COOKIE_KEY));
}

/** 写入邀请码 cookie（原样存，不改大小写）。 */
export function setInviteCodeCookie(value: string): void {
  if (typeof document === "undefined") {
    return;
  }
  const normalized = normalizeInviteCode(value);
  if (!normalized) {
    return;
  }
  Cookies.set(INVITE_CODE_COOKIE_KEY, normalized, withSecureFlag());
}

/** 清除邀请码 cookie（用户在填写页清空 / 注册完成收尾）。 */
export function clearInviteCodeCookie(): void {
  if (typeof document === "undefined") {
    return;
  }
  Cookies.remove(INVITE_CODE_COOKIE_KEY, { path: "/" });
}

/** cookie + store 同步写入。 */
function persistInviteCode(code: string, source: "link" | "manual"): string {
  setInviteCodeCookie(code);
  setInviteCodeInStore(code, source);
  return code;
}

/** 用户在填写页手动提交的邀请码 → cookie + store。 */
export function applyManualInviteCode(value: string): string | null {
  const normalized = normalizeInviteCode(value);
  if (!normalized) {
    return null;
  }
  return persistInviteCode(normalized, "manual");
}

/**
 * 从原生深链 payload 捕获邀请码。两种承载形态都认，**query 优先于 path**：
 * 1. `businessQuery.inviteCode`（原生已过滤 `adj_*` 后的业务 query）
 * 2. `url` 的 `/task/invite/<code>` path 段 —— 原生未知段兜底只带 path，这条是不会丢的那种
 */
export function captureInviteCodeFromDeepLink(
  data:
    | ({ url?: string } & Parameters<typeof extractInviteCodeFromDeepLink>[0])
    | null
    | undefined,
): string | null {
  const code =
    extractInviteCodeFromDeepLink(data) ??
    extractInviteCodeFromPathname(data?.url);
  if (!code) {
    return null;
  }
  return persistInviteCode(code, "link");
}

/**
 * 从当前地址捕获邀请码：优先 path（`/task/invite/<code>`），其次 query（`?inviteCode=`）。
 *
 * path 优先的原因：原生深链未知段兜底只带 path 不带 query
 * （`AdjustDeepLink.mapToNavIntent`），path 形态是两端都不会丢的那条。
 */
export function captureInviteCodeFromLocation(
  pathname: string | null | undefined,
  search: string | null | undefined,
): string | null {
  const fromPath = extractInviteCodeFromPathname(pathname);
  if (fromPath) {
    return persistInviteCode(fromPath, "link");
  }
  if (!search) {
    return null;
  }
  const fromQuery = normalizeInviteCode(
    new URLSearchParams(search).get(INVITE_CODE_QUERY_KEY),
  );
  if (!fromQuery) {
    return null;
  }
  return persistInviteCode(fromQuery, "link");
}

/**
 * 恢复：页面初始化时把 cookie 里的邀请码灌回 store。
 * 用于「上次点了邀请链接、这次才来注册」的场景。
 */
export function restoreInviteCodeFromCookie(): string | null {
  const code = getInviteCodeCookie();
  if (!code) {
    return null;
  }
  setInviteCodeInStore(code, "link");
  return code;
}

/**
 * 清除本地邀请码（cookie + store）。
 *
 * **必须在登录成功后调** —— 邀请码只对**首次注册**生效
 * （后端 `AuthenticationApiDelegateImpl` 判 `!appIds.contains(appId)`），
 * 留着不会再绑定，却会在同设备换账号注册时误带上一个人的码。
 */
export function clearInviteCode(): void {
  clearInviteCodeCookie();
  clearInviteCodeInStore();
}
